/**
 * Execution Guardrails Layer
 * 
 * Translates Delivery Control signals into concrete, proportional
 * execution adjustments that are:
 * - Soft constraints (never hard force)
 * - Visible in logs
 * - Fully reversible
 * - Never mutate contracts, agents, or delivery state
 * - Never bypass pool selection or fairness logic
 * 
 * This is a pure logic module with no side effects.
 */

// --- Types ---

export interface GuardrailInputs {
  /** Pacing cap for the agent (1.0 = no cap, <1.0 = slow down) */
  pacingCap: number;
  /** Pool inclusion boost (0 = no boost, >0 = higher inclusion probability) */
  poolInclusionBoost: number;
  /** Retry priority multiplier (1.0 = normal, >1.0 = higher priority) */
  retryPriorityMultiplier: number;
  /** Guaranteed minimum override (undefined = no override) */
  guaranteedMinimumOverride?: number;
}

export interface AppliedGuardrail {
  type: 'pacing_cap' | 'pool_boost' | 'retry_priority' | 'guaranteed_override';
  agentId: string;
  originalValue: number;
  adjustedValue: number;
  multiplier: number;
  reason: string;
}

export interface GuardrailSummary {
  /** Whether any guardrails were applied */
  guardrailsApplied: boolean;
  /** Pacing cap applied (1.0 = none) */
  pacingCap?: number;
  /** Retry multiplier applied (1.0 = none) */
  retryMultiplier?: number;
  /** Pool boost applied (0 = none) */
  poolBoost?: number;
  /** Guaranteed override applied */
  guaranteedOverride?: number;
  /** Reasons for applied guardrails */
  guardrailReasons: string[];
  /** List of individual guardrail applications */
  appliedGuardrails: AppliedGuardrail[];
}

export interface SafetyCheckResult {
  canProceed: boolean;
  blockReason?: string;
  warningMessages: string[];
}

// --- Constants ---

/** Minimum pacing cap (never reduce below this) */
const MIN_PACING_CAP = 0.5;

/** Maximum pool boost (never boost above this) */
const MAX_POOL_BOOST = 0.6;

/** Maximum retry multiplier (never exceed this) */
const MAX_RETRY_MULTIPLIER = 2.0;

/** Minimum effective score after pacing cap (never reduce to zero) */
const MIN_EFFECTIVE_BID_FLOOR = 1.0;

// --- Core Guardrail Application Functions ---

/**
 * Apply pacing cap to an effective bid.
 * 
 * pacingCap < 1.0 reduces the bid proportionally.
 * The result is clamped to ensure it never goes below MIN_EFFECTIVE_BID_FLOOR
 * and never exceeds the original bid.
 */
export function applyPacingCapToBid(
  originalBid: number,
  pacingCap: number,
  minBid: number = MIN_EFFECTIVE_BID_FLOOR
): { adjustedBid: number; applied: boolean; appliedCap: number } {
  // Clamp pacing cap to safe range
  const safeCap = Math.max(MIN_PACING_CAP, Math.min(1.0, pacingCap));
  
  // If no cap needed (1.0), return original
  if (safeCap >= 1.0) {
    return { adjustedBid: originalBid, applied: false, appliedCap: 1.0 };
  }
  
  // Apply cap as multiplier to bid
  const adjustedBid = Math.max(minBid, Math.min(originalBid, originalBid * safeCap));
  
  return {
    adjustedBid: Math.round(adjustedBid * 100) / 100,
    applied: true,
    appliedCap: safeCap,
  };
}

/**
 * Apply pool inclusion boost to a pool score.
 * 
 * The boost is additive to the final pool score, making the agent
 * more likely to be included in the pool.
 */
export function applyPoolBoost(
  originalScore: number,
  poolBoost: number
): { adjustedScore: number; applied: boolean; appliedBoost: number } {
  // Clamp boost to safe range
  const safeBoost = Math.max(0, Math.min(MAX_POOL_BOOST, poolBoost));
  
  if (safeBoost <= 0) {
    return { adjustedScore: originalScore, applied: false, appliedBoost: 0 };
  }
  
  // Add boost to score
  const adjustedScore = originalScore + safeBoost;
  
  return {
    adjustedScore: Math.round(adjustedScore * 1000) / 1000,
    applied: true,
    appliedBoost: safeBoost,
  };
}

/**
 * Apply retry priority multiplier to a retry score/order.
 * 
 * Higher multiplier = processed earlier in the retry queue.
 */
export function applyRetryPriorityMultiplier(
  originalPriority: number,
  multiplier: number
): { adjustedPriority: number; applied: boolean; appliedMultiplier: number } {
  // Clamp multiplier to safe range
  const safeMultiplier = Math.max(1.0, Math.min(MAX_RETRY_MULTIPLIER, multiplier));
  
  if (safeMultiplier <= 1.0) {
    return { adjustedPriority: originalPriority, applied: false, appliedMultiplier: 1.0 };
  }
  
  // Multiply priority score
  const adjustedPriority = originalPriority * safeMultiplier;
  
  return {
    adjustedPriority: Math.round(adjustedPriority * 100) / 100,
    applied: true,
    appliedMultiplier: safeMultiplier,
  };
}

/**
 * Apply guaranteed minimum override to an agent's delivery target.
 * 
 * This is a temporary selection bias, not a contract mutation.
 * Returns the effective guaranteed minimum to use for scoring.
 */
export function applyGuaranteedMinimumOverride(
  originalGuaranteed: number | undefined,
  override: number | undefined
): { effectiveGuaranteed: number | undefined; applied: boolean } {
  if (override === undefined || override <= 0) {
    return { effectiveGuaranteed: originalGuaranteed, applied: false };
  }
  
  // Use the higher of original or override
  const effective = Math.max(originalGuaranteed ?? 0, override);
  
  return {
    effectiveGuaranteed: effective > 0 ? effective : undefined,
    applied: override > (originalGuaranteed ?? 0),
  };
}

// --- Safety Checks ---

/**
 * Check if execution should proceed based on system state.
 * 
 * Returns a safety check result indicating whether to proceed
 * and any warning messages.
 */
export function performSafetyCheck(
  systemWarnings: Array<{ severity: string; message: string; type?: string }>,
  contractStatuses: Array<{ status: string; contractId: string }> = []
): SafetyCheckResult {
  const warningMessages: string[] = [];
  let canProceed = true;
  let blockReason: string | undefined;
  
  // Check for critical system warnings
  const criticalWarnings = systemWarnings.filter(w => w.severity === 'critical');
  
  if (criticalWarnings.length > 0) {
    // Check for system capacity issues
    const capacityWarning = criticalWarnings.find(w => w.type === 'SYSTEM_CAPACITY_RISK');
    if (capacityWarning) {
      warningMessages.push(`System capacity warning: ${capacityWarning.message}`);
      // Don't block, but add warning
    }
    
    // Check for conflicting signals
    const conflictWarning = criticalWarnings.find(w => w.type === 'CONFLICTING_SIGNALS');
    if (conflictWarning) {
      warningMessages.push(`Signal conflict detected: ${conflictWarning.message}`);
      // Don't block, but add warning
    }
  }
  
  // Check for infeasible contracts (but don't block execution)
  const infeasibleContracts = contractStatuses.filter(c => c.status === 'breached');
  if (infeasibleContracts.length > 0) {
    warningMessages.push(`${infeasibleContracts.length} contract(s) marked as breached`);
    // Note: We emit warning but do NOT block execution
  }
  
  return {
    canProceed,
    blockReason,
    warningMessages,
  };
}

// --- Guardrail Summary Builder ---

/**
 * Build a summary of all guardrails applied to an agent.
 */
export function buildGuardrailSummary(
  agentId: string,
  inputs: GuardrailInputs,
  appliedGuardrails: AppliedGuardrail[] = []
): GuardrailSummary {
  const guardrailReasons: string[] = [];
  
  // Check each input for application
  if (inputs.pacingCap < 1.0) {
    guardrailReasons.push(`Pacing cap: ${(inputs.pacingCap * 100).toFixed(0)}%`);
  }
  
  if (inputs.poolInclusionBoost > 0) {
    guardrailReasons.push(`Pool boost: +${(inputs.poolInclusionBoost * 100).toFixed(0)}%`);
  }
  
  if (inputs.retryPriorityMultiplier > 1.0) {
    guardrailReasons.push(`Retry priority: ${inputs.retryPriorityMultiplier.toFixed(2)}x`);
  }
  
  if (inputs.guaranteedMinimumOverride !== undefined) {
    guardrailReasons.push(`Guaranteed override: ${inputs.guaranteedMinimumOverride}`);
  }
  
  const guardrailsApplied = guardrailReasons.length > 0;
  
  return {
    guardrailsApplied,
    pacingCap: inputs.pacingCap < 1.0 ? inputs.pacingCap : undefined,
    retryMultiplier: inputs.retryPriorityMultiplier > 1.0 ? inputs.retryPriorityMultiplier : undefined,
    poolBoost: inputs.poolInclusionBoost > 0 ? inputs.poolInclusionBoost : undefined,
    guaranteedOverride: inputs.guaranteedMinimumOverride,
    guardrailReasons,
    appliedGuardrails,
  };
}

// --- Batch Guardrail Collection ---

export interface AgentGuardrailsMap {
  [agentId: string]: GuardrailInputs;
}

/**
 * Collect guardrail inputs for multiple agents from control context.
 * 
 * This is a helper for consuming code to batch-collect all guardrails
 * before processing.
 */
export function collectAgentGuardrails(
  agentIds: string[],
  getRetryPriorityMultiplier: (agentId: string) => number,
  getPoolBoost: (agentId: string) => number,
  getPacingCap: (agentId: string) => number,
  getGuaranteedMinOverride: (agentId: string) => number | undefined
): AgentGuardrailsMap {
  const map: AgentGuardrailsMap = {};
  
  for (const agentId of agentIds) {
    map[agentId] = {
      pacingCap: getPacingCap(agentId),
      poolInclusionBoost: getPoolBoost(agentId),
      retryPriorityMultiplier: getRetryPriorityMultiplier(agentId),
      guaranteedMinimumOverride: getGuaranteedMinOverride(agentId),
    };
  }
  
  return map;
}

/**
 * Check if any agent in a map has active guardrails.
 */
export function hasAnyActiveGuardrails(guardrails: AgentGuardrailsMap): boolean {
  for (const agentId of Object.keys(guardrails)) {
    const g = guardrails[agentId];
    if (
      g.pacingCap < 1.0 ||
      g.poolInclusionBoost > 0 ||
      g.retryPriorityMultiplier > 1.0 ||
      g.guaranteedMinimumOverride !== undefined
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Get a list of agents with active guardrails.
 */
export function getAgentsWithGuardrails(guardrails: AgentGuardrailsMap): string[] {
  return Object.entries(guardrails)
    .filter(([, g]) => 
      g.pacingCap < 1.0 ||
      g.poolInclusionBoost > 0 ||
      g.retryPriorityMultiplier > 1.0 ||
      g.guaranteedMinimumOverride !== undefined
    )
    .map(([agentId]) => agentId);
}
